import { Component, DestroyRef, ElementRef, EventEmitter, inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { UsersService } from 'src/app/services/users.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { Organization } from 'src/app/models/organization';
import { Subscription, catchError, switchMap } from 'rxjs';
import { ThemesService } from 'src/app/services/themes.service';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TokenStorage } from 'src/app/core/token-storage';
import { DomainSettings } from 'src/app/models/domain-settings';
import { AppsSettings } from 'src/app/models/apps-settings';
import { SettingsService } from 'src/app/services/settings.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AboutComponent } from '../../dialogs/about/about.component';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { MatFormField, MatFormFieldModule, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatToolbar } from '@angular/material/toolbar';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatTooltip } from '@angular/material/tooltip';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { EnumReasonResetPassword } from 'src/app/models/enum-reason-reset-password';
import { EnumModeResetPassword } from 'src/app/models/enum-mode-reset-password';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { environment } from 'src/environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-nav-top-bar',
    templateUrl: './nav-top-bar.component.html',
    styleUrls: ['./nav-top-bar.component.scss'],
    imports: [
        MatToolbar,
        MatIconButton,
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatFormFieldModule,
        MatMenuItem,
        NgClass,
        ReactiveFormsModule,
        MatLabel,
        MatInput,
        MatAutocompleteTrigger,
        MatSuffix,
        MatAutocomplete,
        MatOption,
        MatButton,
        TranslateModule,
    ],
})
export class NavTopBarComponent implements OnInit, OnDestroy {
    private destroyRef = inject(DestroyRef);

    public faFilter = faFilter;

    aboutDialog: MatDialogRef<AboutComponent>;

    @Output() showMenuEmitter = new EventEmitter<boolean>();
    showMenu = true;
    showOrganizationResearch = true;
    externalApplicationTitle: string;
    currentUser: User;
    currentOrganization: Organization;
    isDarkMode: boolean;

    currentUserChange: Subscription;

    treeData: any[];
    organizationSelected: string = '';
    organizationIdSelected: string;
    isEditable = false;

    searchControl: UntypedFormGroup;

    @ViewChild('search', { static: false }) input: ElementRef;

    organizationsList: Organization[];
    organizationsFilter: Organization[] = [];

    currentOrganizationChange: Subscription;

    private externalParametersChange: Subscription;

    domainSettings: DomainSettings;
    cesarApplications: AppsSettings[] = [];

    private OFFICE_DOMAIN_PROPERTY: string = 'ms-domain';

    constructor(
        private authenticationService: AuthenticationService,
        private userService: UsersService,
        private organizationsService: OrganizationsService,
        private dialog: MatDialog,
        private router: Router,
        private translate: TranslateService,
        private settingsService: SettingsService,
        private themesService: ThemesService,
        private formBuilder: UntypedFormBuilder,
        private tokenStorage: TokenStorage,
        private toastr: ToastrService,
        private spinner: NgxSpinnerService
    ) {
        this.currentUserChange = userService.currentUserChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
            this.currentUser = user;
        });

        settingsService
            .getDomainSetting()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                this.domainSettings = result;

                if (this.domainSettings) {
                    for (const [app, settings] of Object.entries(this.domainSettings.apps)) {
                        let jsonSettings = JSON.stringify(settings);
                        this.cesarApplications.push(JSON.parse(jsonSettings));
                    }
                }
            });
    }

    ngOnInit() {
        this.organizationSelected = this.organizationsService.getCurrentOrganization() ? this.organizationsService.getCurrentOrganization().display : '';

        this.themesService.currentTheme$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((theme) => {
            this.isDarkMode = theme.includes('dark') ? true : false;
        });

        this.searchControl = this.formBuilder.group({
            organizationControl: [''],
            organization: [this.currentOrganization?.display],
        });

        this.searchControl
            .get('organizationControl')
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                value = typeof value === 'string' ? value : value ? value.display : '';
                this.searchOrganization(value);
            });

        this.currentOrganizationChange = this.organizationsService.currentOrganizationChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((o) => {
            this.currentOrganization = o;

            if (this.searchControl && this.currentOrganization) {
                this.searchControl.get('organization').setValue(this.currentOrganization.display);
            }
        });
    }

    displayWith(element: any) {
        return element ? element.display : null;
    }

    ngOnDestroy() {
        this.currentUserChange.unsubscribe();
        this.currentOrganizationChange.unsubscribe();
        this.externalParametersChange.unsubscribe();
    }

    onEdit() {
        this.isEditable = true;
        setTimeout(() => {
            this.input.nativeElement.focus();
        }, 100);
    }

    searchOrganization(value: string): void {
        if (value.length > 2) {
            this.organizationsService
                .getOrganisations(false, `display like '%${value}%'`)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((x) => {
                    this.organizationsFilter = x;
                });
        } else {
            this.organizationsFilter.splice(0, this.organizationsFilter.length);
        }
    }

    logout() {
        this.authenticationService.logout(this.currentUser.account);
        this.router.navigate(['/login']);
    }

    menuClick() {
        this.showMenu = !this.showMenu;
        this.showMenuEmitter.emit(this.showMenu);
    }

    frenchLanguage() {
        this.translate.setDefaultLang('fr');
        this.translate.use('fr');
        sessionStorage.setItem('language', 'fr');
    }

    englishLanguage() {
        this.translate.setDefaultLang('en');
        this.translate.use('en');
        sessionStorage.setItem('language', 'en');
    }

    onThemeChange() {
        this.themesService.changeTheme(this.isDarkMode ? 'avm-light-theme' : 'avm-dark-theme');
    }

    focusout() {
        this.searchControl.get('organization').setValue(this.currentOrganization.display);
        this.isEditable = false;
    }

    focus() {
        this.searchControl.get('organizationControl').setValue('');
    }

    selectOrganization(organization: Organization) {
        this.organizationsService.setCurrentOrganization(organization);
    }

    getUrl(url: string) {
        if (this.currentUser) {
            return `${url}?__access_token__=${this.tokenStorage.getToken()}`;
        } else {
            return `${url}`;
        }
    }

    about() {
        this.aboutDialog = this.dialog.open(AboutComponent, {
            panelClass: 'default-dialog',
        });
    }

    modifyPassword() {
        if (this.currentOrganization.properties.filter((a) => a.name === this.OFFICE_DOMAIN_PROPERTY).length > 0) {
            this.toastr.warning(this.translate.instant('ForgottenPassword.ResetMicrosoftAccountError'));
        } else {
            this.spinner.show();

            this.settingsService
                .getDomainSetting()
                .pipe(
                    switchMap((x) => {
                        let gumUrl: string;
                        try {
                            gumUrl = x.apps['gum']['url'];
                        } catch {
                            gumUrl = null;
                        }

                        if (!environment.production) {
                            gumUrl = 'http://localhost:4100';
                        }

                        if (gumUrl) {
                            let parameters = new ExternalParameters();
                            parameters.name = 'Centrex UI';
                            parameters.return = encodeURIComponent(`${window.location.origin}/instance`);
                            let redirectUrl = encodeURIComponent(`${gumUrl}/changepassword?resetPassword=${this.currentUser.id}&parameters=${JSON.stringify(parameters)}`);

                            return this.userService.StartPasswordReset(this.currentUser.id, EnumReasonResetPassword.Modify, EnumModeResetPassword.Mail, redirectUrl);
                        } else {
                            throw 'Domain Error';
                        }
                    }),
                    catchError((err) => {
                        throw err;
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: () => {
                        this.spinner.hide();

                        const index = this.currentUser.properties.findIndex((a) => a.name === 'Email');
                        const emailContact = index ? this.currentUser.properties[index].value : this.currentUser.account;

                        this.toastr.success(this.translate.instant('ForgottenPassword.ResetMessage', { email: emailContact }), '', {
                            enableHtml: true,
                        });
                    },
                    error: (err: unknown) => {
                        this.spinner.hide();

                        if (err === 'Domain Error') {
                            this.toastr.warning(this.translate.instant('Login.DomainWarning'), '', { enableHtml: true });
                        } else {
                            const error = err as HttpErrorResponse;

                            if (error.status === 412) {
                                this.toastr.error(this.translate.instant('ForgottenPassword.EmailError'), '', { enableHtml: true });
                            } else {
                                this.toastr.error(this.translate.instant('ForgottenPassword.Error'), '', { enableHtml: true });
                            }
                        }
                    },
                });
        }
    }
}
